import { Link } from 'react-router-dom';
import SEO from '@/components/seo/SEO';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bot, ArrowRight } from 'lucide-react';
import { AGENTS } from '@/data/agents';

const SITE = 'https://getbitsy.ai';

const COLORS = {
  amber:   { bg: 'bg-amber-500/15',   text: 'text-amber-400' },
  cyan:    { bg: 'bg-cyan-500/15',    text: 'text-cyan-400' },
  emerald: { bg: 'bg-emerald-500/15', text: 'text-emerald-400' },
  teal:    { bg: 'bg-teal-500/15',    text: 'text-teal-400' },
  orange:  { bg: 'bg-orange-500/15',  text: 'text-orange-400' },
};

export default function AgentsIndexPage() {
  const agents = Object.values(AGENTS);

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'getbitsy.ai Hotel AI Agents',
    description: 'Five autonomous AI agents that run hotels on the BHERMES Protocol — Bitsy (concierge), Marina (operations), Sage (revenue), Argus (onboarding), Hermes (verification).',
    numberOfItems: agents.length,
    itemListElement: agents.map((a, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${SITE}/agents/${a.slug}`,
      name: a.name,
      description: a.role,
    })),
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100" data-testid="agents-index-page">
      <SEO
        title="Hotel AI Agents — Bitsy, Marina, Sage, Argus, Hermes"
        description="Five autonomous Hotel AI Agents that run getbitsy.ai. Bitsy (concierge), Marina (operations), Sage (revenue), Argus (onboarding), Hermes (verification). Compare to Mews, Cloudbeds, Duetto."
        canonical={`${SITE}/agents`}
        schema={itemListSchema}
      />

      <header className="border-b border-zinc-800/60 px-6 py-4 flex justify-between items-center">
        <Link to="/" className="text-sm tracking-widest text-zinc-400 hover:text-zinc-200">← getbitsy.ai</Link>
        <Link to="/docs" className="text-xs tracking-widest text-zinc-500 hover:text-zinc-300">Documentation</Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16 space-y-12">
        <section>
          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 mb-4">PROTOCOL AGENTS</Badge>
          <h1 className="text-5xl font-semibold leading-tight">
            Five AI agents <span className="text-amber-400">run the hotel</span>.
          </h1>
          <p className="text-lg text-zinc-400 mt-4 max-w-3xl leading-relaxed">
            getbitsy.ai is an Agentic Hospitality OS — five autonomous AI agents replace the booking
            engine, channel manager, AI chatbot, revenue manager, and onboarding team that traditional
            hotels stitch together with a dozen SaaS subscriptions.
          </p>
        </section>

        <section className="grid md:grid-cols-2 gap-4">
          {agents.map((a) => {
            const c = COLORS[a.color] || COLORS.amber;
            return (
            <Link key={a.slug} to={`/agents/${a.slug}`} data-testid={`agent-link-${a.slug}`}>
              <Card className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition h-full">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl ${c.bg} flex items-center justify-center`}>
                      <Bot className={`w-6 h-6 ${c.text}`} />
                    </div>
                    <div>
                      <p className="text-xl font-semibold">{a.name}</p>
                      <p className="text-xs text-zinc-500 tracking-widest">{a.role.toUpperCase()}</p>
                    </div>
                  </div>
                  <p className="text-zinc-300 text-sm leading-relaxed">{a.tagline}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-zinc-500">vs {a.competitors.map((cp) => cp.name).join(', ')}</span>
                    <ArrowRight className="w-4 h-4 text-zinc-500" />
                  </div>
                </CardContent>
              </Card>
            </Link>
            );
          })}
        </section>

        <section className="border-t border-zinc-800 pt-10">
          <h2 className="text-2xl font-semibold mb-4">How the agents work together</h2>
          <p className="text-zinc-400 leading-relaxed max-w-3xl">
            <Link to="/agents/argus" className="text-amber-400 hover:underline">Argus</Link> onboards a new hotel in 4
            minutes. <Link to="/agents/hermes" className="text-amber-400 hover:underline">Hermes</Link> autonomously
            verifies it and mints the HOTEL SBT. Once live, <Link to="/agents/bitsy" className="text-amber-400 hover:underline">Bitsy</Link> handles
            every guest interaction, <Link to="/agents/marina" className="text-amber-400 hover:underline">Marina</Link> auto-restocks
            via Smart Tray IoT, and <Link to="/agents/sage" className="text-amber-400 hover:underline">Sage</Link> runs
            continuous A/B pricing experiments. No human staff required for routine operations.
          </p>
        </section>
      </main>

      <footer className="border-t border-zinc-800/60 text-center py-6 text-[11px] text-zinc-600 tracking-wider">
        Powered by BHERMES · M.O.A.T. · <Link to="/" className="hover:text-zinc-400">getbitsy.ai</Link>
      </footer>
    </div>
  );
}
